import { useState, type FormEvent, type ReactNode } from 'react';
import { useShellTexts } from '../i18n/use-shell-texts';
import { inputClass } from '../kit/form/input';
import { Button } from '../kit/button';
import { Icon } from '../kit/icon';
import { Field } from './auth-card';

/** The backend's rule (reset-password.schema.ts `minLength: 8`), checked before sending. */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * New password + confirmation, optionally preceded by the current one. Validates length and
 * match itself; `onSubmit` gets the values only when both hold.
 */
export function PasswordForm({
  askCurrent,
  submitLabel,
  submittingLabel,
  submitting,
  onSubmit,
  children,
}: {
  askCurrent: boolean;
  submitLabel: string;
  submittingLabel: string;
  submitting: boolean;
  onSubmit: (values: { current: string; next: string }) => void;
  children?: ReactNode;
}) {
  const t = useShellTexts().resetPassword;
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState(false);

  const tooShort = touched && next.length < MIN_PASSWORD_LENGTH ? t.tooShort : null;
  const mismatch = touched && confirm !== next ? t.mismatch : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);
    if ((askCurrent && !current) || next.length < MIN_PASSWORD_LENGTH || confirm !== next) {
      return;
    }
    onSubmit({ current, next });
  }

  return (
    <form onSubmit={submit} noValidate>
      {children}
      {askCurrent ? (
        <Field id="current-password" label={t.currentPassword} addon={<Icon name="lock" />}>
          <input
            id="current-password"
            type="password"
            autoComplete="current-password"
            className={inputClass}
            placeholder={t.enterCurrentPassword}
            value={current}
            onChange={event => setCurrent(event.target.value)}
            aria-invalid={touched && !current ? true : undefined}
          />
        </Field>
      ) : null}
      <Field id="new-password" label={t.newPassword} error={tooShort} addon={<Icon name="lock" />}>
        <input
          id="new-password"
          type="password"
          autoComplete="new-password"
          className={inputClass}
          placeholder={t.enterNewPassword}
          value={next}
          onChange={event => setNext(event.target.value)}
          aria-invalid={tooShort ? true : undefined}
          aria-describedby={tooShort ? 'new-password-error' : undefined}
        />
      </Field>
      <Field
        id="confirm-password"
        label={t.confirmPassword}
        error={mismatch}
        addon={<Icon name="lock" />}
      >
        <input
          id="confirm-password"
          type="password"
          autoComplete="new-password"
          className={inputClass}
          placeholder={t.confirmPasswordPlaceholder}
          value={confirm}
          onChange={event => setConfirm(event.target.value)}
          aria-invalid={mismatch ? true : undefined}
          aria-describedby={mismatch ? 'confirm-password-error' : undefined}
        />
      </Field>
      <Button type="submit" variant="primary" className="px-6" loading={submitting}>
        {submitting ? submittingLabel : submitLabel}
      </Button>
    </form>
  );
}
